import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import ProgressBar from './common/ProgressBar';
import ExportModal from './common/ExportModal';

const { FiBookOpen, FiTarget, FiLayers, FiTrendingUp, FiEdit, FiCheck, FiClock, FiArrowRight, FiLogOut, FiDownload } = FiIcons;

const Dashboard = ({ moduleData, progress, onLogout }) => {
  const [isExportOpen, setIsExportOpen] = useState(false);

  const modules = [
    {
      id: 'module1',
      title: 'Clarify the Calling',
      description: 'Discern why this book exists, who it\'s for, and the transformation God intends it to bring.',
      icon: FiTarget,
      path: '/module-1',
      color: 'from-blue-500 to-blue-600'
    },
    {
      id: 'module2',
      title: 'Structure the Story or Message',
      description: 'Create a clear roadmap for your book — fiction or nonfiction — so momentum replaces overwhelm.',
      icon: FiLayers,
      path: '/module-2',
      color: 'from-orange-500 to-orange-600'
    },
    {
      id: 'module3',
      title: 'Build Your Book Plan',
      description: 'Turn vision into a realistic, grace-filled plan that fits real life.',
      icon: FiBookOpen,
      path: '/module-3',
      color: 'from-blue-500 to-orange-500'
    },
    {
      id: 'module4',
      title: 'Sustain Momentum',
      description: 'Design a writing rhythm that supports faithfulness over perfection.',
      icon: FiTrendingUp,
      path: '/module-4',
      color: 'from-orange-500 to-blue-500'
    },
    {
      id: 'module5',
      title: 'Kickstart the First Chapter',
      description: 'Break the blank page and begin writing today.',
      icon: FiEdit,
      path: '/module-5',
      color: 'from-blue-600 to-orange-600'
    }
  ];

  const getModuleStatus = (moduleId) => {
    const module = moduleData[moduleId];
    if (module.completed) return 'complete';
    if (Object.keys(module.data).length > 0) return 'in-progress';
    return 'not-started';
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'complete': return FiCheck;
      case 'in-progress': return FiClock;
      default: return null;
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'complete': return 'text-green-500';
      case 'in-progress': return 'text-orange-500';
      default: return 'text-gray-400';
    }
  };

  const completedModulesCount = Object.values(moduleData).filter(m => m.completed).length;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 to-blue-50 pb-20">
      {/* Header */}
      <div className="bg-white shadow-sm border-b sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 py-4">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-3">
              <div className="w-10 h-10 bg-[#2558ab] rounded-xl flex items-center justify-center text-white font-bold">P</div>
              <div>
                <h1 className="text-xl font-bold text-gray-900 leading-tight">Planned with Purpose</h1>
                <div className="flex items-center text-[10px] text-orange-600 font-bold uppercase tracking-tighter">
                  <span>GospelWise Author</span>
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 sm:space-x-4">
              <button
                onClick={() => setIsExportOpen(true)}
                className="hidden sm:flex items-center space-x-2 px-4 py-2 bg-blue-50 text-blue-700 rounded-lg text-sm font-bold hover:bg-blue-100 transition-colors"
              >
                <SafeIcon icon={FiDownload} />
                <span>Export Workbook</span>
              </button>
              
              <button
                onClick={onLogout}
                className="flex items-center space-x-2 px-3 sm:px-4 py-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg text-sm font-medium transition-all"
              >
                <SafeIcon icon={FiLogOut} />
                <span className="hidden sm:inline">Logout</span>
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-4xl mx-auto px-4 py-8">
        {/* Progress Summary */}
        <div className="flex flex-col md:flex-row gap-6 mb-10">
          <div className="flex-1 bg-white rounded-3xl p-8 shadow-sm border border-slate-100">
            <div className="flex items-center justify-between mb-6">
              <div>
                <h2 className="text-2xl font-bold text-slate-800">Your Progress</h2>
                <p className="text-slate-500 text-sm">Modules completed: {completedModulesCount}/5</p>
              </div>
              <div className="text-right">
                <span className="text-4xl font-black text-[#2558ab]">{progress}%</span>
              </div>
            </div>
            <ProgressBar progress={progress} />
          </div>

          <button 
            onClick={() => setIsExportOpen(true)}
            className="md:w-64 bg-gradient-to-br from-[#2558ab] to-[#ff8057] rounded-3xl p-8 text-white shadow-xl shadow-blue-100 flex flex-col items-center justify-center text-center group hover:scale-[1.02] transition-transform"
          >
            <SafeIcon icon={FiDownload} className="text-4xl mb-3 group-hover:animate-bounce" />
            <span className="font-bold text-lg">Export Your Work</span>
            <span className="text-xs opacity-80 mt-1">PDF & Word Format</span>
          </button>
        </div>

        {/* Module Grid */}
        <div className="space-y-4">
          {modules.map((module, index) => {
            const status = getModuleStatus(module.id);
            const StatusIcon = getStatusIcon(status);
            
            return (
              <motion.div
                key={module.id}
                initial={{ opacity: 0, x: -20 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: index * 0.1 }}
              >
                <Link to={module.path} className="block group">
                  <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm group-hover:shadow-md group-hover:border-blue-200 transition-all">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-6">
                        <div className={`w-14 h-14 rounded-2xl bg-gradient-to-br ${module.color} flex items-center justify-center text-white shadow-lg`}>
                          <SafeIcon icon={module.icon} className="text-2xl" />
                        </div>
                        <div>
                          <div className="flex items-center space-x-3 mb-1">
                            <h3 className="text-lg font-bold text-slate-800 group-hover:text-[#2558ab]">{module.title}</h3>
                            {StatusIcon && <SafeIcon icon={StatusIcon} className={`text-sm ${getStatusColor(status)}`} />}
                          </div>
                          <p className="text-slate-500 text-sm max-w-md hidden sm:block">{module.description}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-4">
                        <span className={`px-3 py-1 rounded-full text-[10px] font-bold uppercase tracking-wider ${
                          status === 'complete' ? 'bg-green-50 text-green-600' :
                          status === 'in-progress' ? 'bg-orange-50 text-orange-600' :
                          'bg-slate-50 text-slate-400'
                        }`}>
                          {status.replace('-', ' ')}
                        </span>
                        <SafeIcon icon={FiArrowRight} className="text-slate-300 group-hover:text-[#2558ab] group-hover:translate-x-1 transition-all" />
                      </div>
                    </div>
                  </div>
                </Link>
              </motion.div>
            );
          })}
        </div>

        {/* Final CTA */}
        {progress === 100 && (
          <motion.div
            initial={{ opacity: 0, scale: 0.9 }}
            animate={{ opacity: 1, scale: 1 }}
            className="mt-12 bg-white rounded-[2rem] p-10 border-4 border-blue-50 shadow-2xl text-center"
          >
            <div className="mb-6 inline-block p-4 bg-orange-50 rounded-3xl">
              <SafeIcon icon={FiCheck} className="text-5xl text-orange-500" />
            </div>
            <h2 className="text-3xl font-black text-slate-800 mb-4">Planning Complete!</h2>
            <p className="text-slate-600 text-lg mb-8 max-w-xl mx-auto italic">
              "You've planned with purpose. Now write with faith."
            </p>
            <div className="flex flex-wrap gap-4 justify-center">
              <button 
                onClick={() => setIsExportOpen(true)}
                className="px-8 py-4 bg-[#2558ab] text-white rounded-2xl font-bold shadow-lg hover:bg-blue-800 transition-all flex items-center space-x-2"
              >
                <SafeIcon icon={FiDownload} />
                <span>Export My Workbook</span>
              </button>
              <button className="px-8 py-4 bg-slate-100 text-slate-700 rounded-2xl font-bold hover:bg-slate-200 transition-all">
                Return to Course
              </button>
            </div>
          </motion.div>
        )}
      </div>

      <ExportModal 
        isOpen={isExportOpen} 
        onClose={() => setIsExportOpen(false)} 
        moduleData={moduleData} 
      />
    </div>
  );
};

export default Dashboard;